import { Controller } from "@hotwired/stimulus";

// The card form (fire/shared/_card, docs/PAY.md § The card lanes). One form
// for both gateways; the payee's decides the fields:
//
//   asaas   plain inputs; the form posts card[...] once, straight into the
//           gateway's tokenize call on the server (the number grouped as it
//           is typed by card-input, where a host registers fire's inputs)
//   stripe  Stripe's own card box mounted in `mount`; on submit Stripe.js
//           makes the PaymentMethod in the browser and only its id goes,
//           in card[pm]. The number never reaches this page, nor us
//
// A kept card picked (card_id), the new card's fieldset is disabled: it is
// neither posted nor required. Core, no npm dependency: every host has it.
//
// The wallet (docs/PAY.md § Wallets), where the form carries a wallet key:
// Stripe's Express Checkout Element in `wallet`, its box `express` shown only
// when Stripe finds Apple Pay or Google Pay on this device. Confirmed in the
// wallet's sheet, Stripe.js makes a ConfirmationToken and only its ctoken_
// goes, in wallet[token], the card's own fields left out of the post.
//
//   <form data-controller="card-form" data-card-form-gateway-value="stripe"
//         data-card-form-key-value="pk_..." data-card-form-account-value="acct_..."
//         data-card-form-wallet-key-value="pk_..." data-card-form-amount-value="4900">
const STRIPE_JS = "https://js.stripe.com/v3/";

// Stripe.js comes from Stripe, never bundled (its terms, and PCI's SAQ A).
// One tag per page, however many forms ask.
const stripeJs = () =>
  window.Stripe
    ? Promise.resolve(window.Stripe)
    : (window.__stripeJs ||= new Promise((resolve, reject) => {
        const tag = document.createElement("script");
        tag.src = STRIPE_JS;
        tag.onload = () => resolve(window.Stripe);
        tag.onerror = reject;
        document.head.appendChild(tag);
      }));

// Stripe's box takes hex or rgb only, and the theme's tokens resolve to
// oklch/oklab: a token, resolved by the page and read back as sRGB pixels.
const srgb = (color) => {
  const ink = document
    .createElement("canvas")
    .getContext("2d", { willReadFrequently: true });
  ink.fillStyle = color;
  ink.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ink.getImageData(0, 0, 1, 1).data;
  // tokens:ok the page's own token, read back from a canvas for Stripe Elements; no colour is chosen here.
  return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(2)})`;
};

export default class extends Controller {
  static targets = [
    "name",
    "mount",
    "pm",
    "fresh",
    "error",
    "express",
    "wallet",
    "token",
  ];
  static values = {
    gateway: String,
    key: String,
    account: String,
    locale: String,
    down: String,
    walletKey: String,
    amount: Number,
  };

  connect() {
    // Stripe.js that never came (a blocker, a network): her words, never a silent button.
    if (this.gatewayValue === "stripe" && this.hasMountTarget)
      this.mountStripe().catch(() => this.down());
    // No wallet is no loss: the box stays hidden and the card form is the page.
    if (this.walletKeyValue && this.hasWalletTarget)
      this.mountWallet().catch(() => {});
  }

  disconnect() {
    this.box?.destroy();
    this.box = null;
    this.express?.destroy();
    this.express = null;
  }

  // The house's Stripe, for the wallet's sheet: the bill's centavos only draw
  // it, the server opens the intent for its own. Card only: Apple Pay and
  // Google Pay, never Link, PayPal, Klarna or Amazon Pay.
  async mountWallet() {
    const Stripe = await stripeJs();
    this.walletStripe = Stripe(this.walletKeyValue, {
      locale: this.localeValue || "auto",
    });
    this.walletElements = this.walletStripe.elements({
      mode: "payment",
      amount: this.amountValue,
      currency: "brl",
      paymentMethodTypes: ["card"],
    });
    this.express = this.walletElements.create("expressCheckout", {
      paymentMethods: {
        link: "never",
        paypal: "never",
        klarna: "never",
        amazonPay: "never",
      },
    });
    this.express.on("ready", ({ availablePaymentMethods }) => {
      this.expressTarget.hidden = !availablePaymentMethods;
    });
    this.express.on("confirm", (event) => this.confirmWallet(event));
    this.express.mount(this.walletTarget);
  }

  // The sheet said yes: the ctoken_ goes, alone. A kept card's radio and the
  // new card's fields are disabled first, so they neither post nor stop the
  // post as empty required fields.
  async confirmWallet(event) {
    const { error: unfit } = await this.walletElements.submit();
    if (unfit) return event.paymentFailed?.();
    const { confirmationToken, error } =
      await this.walletStripe.createConfirmationToken({
        elements: this.walletElements,
      });
    if (error) {
      this.errorTarget.textContent = error.message;
      return event.paymentFailed?.();
    }
    this.tokenTarget.value = confirmationToken.id;
    if (this.hasFreshTarget) this.freshTarget.disabled = true;
    this.element
      .querySelectorAll("input[name=card_id]")
      .forEach((radio) => (radio.disabled = true));
    setTimeout(() => this.element.requestSubmit());
  }

  // A kept card or a new one: only the new one's fields ride the post.
  toggle() {
    if (!this.hasFreshTarget) return;
    const picked = this.element.querySelector("input[name=card_id]:checked");
    this.freshTarget.disabled = Boolean(picked && picked.value !== "");
  }

  // Stripe only, and only for a new card: Stripe.js makes the PaymentMethod
  // first, then the form goes with its id and nothing else of the card.
  async submit(event) {
    if (
      this.gatewayValue !== "stripe" ||
      this.freshTarget.disabled ||
      this.pmTarget.value
    )
      return;
    event.preventDefault();
    if (!this.stripe) return this.down();
    const { paymentMethod, error } = await this.stripe.createPaymentMethod({
      type: "card",
      card: this.box,
      billing_details: { name: this.nameTarget.value },
    });
    if (error) {
      this.errorTarget.textContent = error.message;
      return;
    }
    this.errorTarget.textContent = "";
    this.pmTarget.value = paymentMethod.id;
    // A task of its own: an answer that came back inside this submit's own
    // dispatch would have the browser drop the second submit silently.
    setTimeout(() => this.element.requestSubmit(event.submitter));
  }

  down() {
    this.errorTarget.textContent = this.downValue;
  }

  // Stripe's box wears the page's own ink and type, read off the name field:
  // the theme's tokens, resolved, never a colour of ours written here.
  async mountStripe() {
    const Stripe = await stripeJs();
    this.stripe = Stripe(this.keyValue, {
      locale: this.localeValue || "auto",
      ...(this.accountValue ? { stripeAccount: this.accountValue } : {}),
    });
    const look = getComputedStyle(this.nameTarget);
    const hint = getComputedStyle(this.nameTarget, "::placeholder");
    this.box = this.stripe.elements().create("card", {
      hidePostalCode: true,
      style: {
        base: {
          color: srgb(look.color),
          fontFamily: look.fontFamily,
          fontSize: look.fontSize,
          "::placeholder": { color: srgb(hint.color) },
        },
      },
    });
    this.box.mount(this.mountTarget);
  }
}
